import React, { useCallback, useEffect, useRef, useState } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { Link } from 'react-router-dom';
import { useRefreshable } from '../context/RefreshContext';
import { useAuth } from '../context/AuthContext';
import { useBasket } from '../context/BasketContext';
import { useT } from '../i18n';
import UserMenu from '../components/UserMenu';
import NavBrand from '../components/NavBrand';
import PageSpinner from '../components/PageSpinner';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import BackBar from '../components/BackBar';
import PersonIcon from '../components/PersonIcon';
import { ConfirmDialog } from '../components/GalleryDialogs';
import '../components/GalleryManager.css';
import { fetchBasketRows, removeFromBasket, clearBasket } from '../firebase/basketService';
import { getGalleryItem } from '../firebase/galleryService';
import { ITEM_KINDS } from '../constants/gallery';
import { fetchShop } from '../firebase/shopService';
import { currencySymbolOf } from '../constants/designer';
import { productHref, hasPrice } from '../constants/gallery';
import './SearchPage.css';
import './Dashboard.css';
import './MyDesigners.css';
import './Basket.css';

// ── MY BASKET ────────────────────────────────────────────────────────────────
// The products set aside from shops around Tunify.
//
// A SAVED LIST, NOT A CART. Tunify takes no payment — each row links out to the
// shop's own page, or to the shop's profile so it can be messaged. So the page
// is honest about what it is: things you found and want to come back to, with a
// current price and a way to reach the seller.
//
// THE ROWS ARE RESOLVED HERE. The stored basket carries nothing but photo ids
// (see basketService for why), so opening this page reads each product — which
// is what makes the price current and lets a row that has been taken down say
// so instead of quoting a listing that no longer exists.
export default function Basket() {
  const { user } = useAuth();
  const { ids } = useBasket();
  const { t } = useT();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  // ── ASK BEFORE REMOVING ───────────────────────────────────────────────────
  // Both removals used to happen on the press. Emptying the basket is one
  // click away from wiping a list somebody built over weeks, and the single ✕
  // sits in the corner of a card whose whole face is a link — a thumb aiming
  // for the product lands on it easily.
  //
  // Neither is undoable: the rows hold nothing but ids, so nothing is kept to
  // put back. A question costs one tap and is the only safeguard there is.
  //
  // '' | 'all' | <photoId> — what is being confirmed, so one piece of state
  // serves both dialogs rather than two booleans that can disagree.
  const [confirming, setConfirming] = useState('');

  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    if (!user?.uid) { setLoading(false); return; }
    try {
      const saved = await fetchBasketRows(user.uid);

      // Products and their shops, in parallel. A product that has been deleted
      // or hidden comes back null — the rules refuse a hidden item, which is
      // correct behaviour and has to read as "unavailable", not as an error.
      const resolved = await Promise.all(saved.map(async (row) => {
        const [product, shop] = await Promise.all([
          getGalleryItem(ITEM_KINDS.PHOTO, row.photoId).catch(() => null),
          row.shopUid ? fetchShop(row.shopUid).catch(() => null) : null,
        ]);
        return { ...row, product, shop };
      }));

      if (alive.current) setRows(resolved);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { load(); }, [load]);
  useRefreshable(() => load());

  // Removing elsewhere (the lightbox, another tab) shrinks the id set, and a
  // row for something no longer in the basket would linger until a reload.
  // Filtered rather than re-fetched: the answer is already known.
  useEffect(() => {
    setRows(prev => prev.filter(r => ids.has(r.photoId)));
  }, [ids]);

  async function remove(photoId) {
    const snapshot = rows;
    setRows(prev => prev.filter(r => r.photoId !== photoId));   // optimistic
    try {
      await removeFromBasket(user.uid, photoId);
    } catch {
      setRows(snapshot);
    }
  }

  async function empty() {
    if (clearing) return;
    setClearing(true);
    const snapshot = rows;
    setRows([]);
    try {
      await clearBasket(user.uid);
    } catch {
      setRows(snapshot);
    } finally {
      setClearing(false);
    }
  }

  return (
    <div className="dash-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <Link to="/login" className="sp-signin">{t('nav_login')}</Link>}
        </div>
      </header>

      <BackBar width={1240} />

      <main className="dash-main bsk-main">
        <div className="bsk-head">
          <h1 className="sp-title bsk-title">{t('bsk_title')}</h1>
          {rows.length > 0 && (
            <button
              type="button"
              className="bsk-clear"
              onClick={() => setConfirming('all')}
              disabled={clearing}
            >
              {t('bsk_clear')}
            </button>
          )}
        </div>

        {loading ? (
          <PageSpinner />
        ) : rows.length === 0 ? (
          <p className="sp-empty">{t('bsk_empty')}</p>
        ) : (
          <ul className="bsk-list">
            {rows.map(({ photoId, product, shop }) => {
              // Deleted, hidden, or unreadable — all the same to a reader, and
              // all worth keeping on screen with a way to clear it rather than
              // silently vanishing.
              const gone = !product;
              return (
                <li className={`bsk-row${gone ? ' bsk-row--gone' : ''}`} key={photoId}>
                  {/* THE WHOLE CARD OPENS THE PRODUCT — a transparent LINK
                      stretched across it, sitting UNDER the shop link and the
                      two actions (which are raised above it in the CSS).
                      Wrapping those in the link instead would be an anchor
                      inside an anchor; this way the card is one control with
                      one accessible name, and the links inside it stay their
                      own.

                      A LINK, NOT A BUTTON THAT OPENED A LIGHTBOX. It used to
                      spring the viewer over this page, which meant a basket row
                      could not be opened in a new tab or sent to anyone. The
                      product's own page carries everything the viewer did and
                      more — Call, Message, the full description — and Back
                      returns here.

                      Nothing to open when the product is gone. */}
                  {!gone && (
                    <Link
                      className="bsk-open"
                      to={`/product/${photoId}`}
                      aria-label={product.title || t('gal_open')}
                      title={t('gal_open')}
                    />
                  )}

                  {/* TOP-RIGHT, pinned to the card's corner. It used to sit in
                      the action row, which put it below the buy button on a
                      phone — the destructive control furthest down the card
                      and closest to the thumb. Remove belongs where a card's
                      dismiss always is. */}
                  <button
                    type="button"
                    className="bsk-remove"
                    onClick={() => setConfirming(photoId)}
                    aria-label={t('bsk_remove')}
                    title={t('bsk_remove')}
                  >✕</button>

                  <div className="bsk-thumb">
                    {product?.thumbUrl
                      ? <img src={product.thumbUrl} alt="" loading="lazy" />
                      : <span className="bsk-thumb-ph" aria-hidden="true">?</span>}
                    {!gone && (
                      <span className="bsk-thumb-veil" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round">
                          <circle cx="11" cy="11" r="7" />
                          <path d="m20 20-3.2-3.2" />
                          <path d="M11 8.2v5.6M8.2 11h5.6" />
                        </svg>
                      </span>
                    )}
                  </div>

                  <div className="bsk-body">
                    {gone ? (
                      <span className="bsk-gone">{t('bsk_unavailable')}</span>
                    ) : (
                      <>
                        {/* The name is what a basket row is FOR — a column of
                            prices with no names is a receipt, not a list of
                            things. Falls back to the description for listings
                            published before the field existed. */}
                        {(product.title || product.description) && (
                          <span className="bsk-name">
                            {product.title || product.description}
                          </span>
                        )}
                        {product.title && product.description && (
                          <span className="bsk-desc">{product.description}</span>
                        )}
                        {hasPrice(product) && (
                          <span className="bsk-price">
                            {product.price} {currencySymbolOf(product.currency)}
                          </span>
                        )}
                      </>
                    )}

                    {shop && (
                      <Link to={`/shop/${shop.uid || shop.id}`} className="bsk-shop">
                        <span className="bsk-shop-avatar">
                          {shop.avatarUrl ? <img src={shop.avatarUrl} alt="" /> : <PersonIcon />}
                        </span>
                        {shop.name}
                      </Link>
                    )}
                  </div>

                  {/* The only route to a purchase — Tunify takes no payment, so
                      the shop's own page is where this ends. Absent when the
                      shop gave no link; the profile above is then the way to
                      reach them. */}
                  {!gone && product.productUrl && (
                    <div className="bsk-actions">
                      <a
                        className="bsk-buy"
                        href={productHref(product.productUrl)}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                      >{t('gal_buy')}</a>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {/* THE SAME DIALOG THE GALLERY DELETES THROUGH — see GalleryDialogs. It
          replaces window.confirm, which cannot be translated and is suppressed
          outright by some mobile browsers, which would have made a delete
          silently do nothing. One component, two questions. */}
      {confirming === 'all' ? (
        <ConfirmDialog
          title={t('bsk_clear_title')}
          message={t('bsk_clear_msg').replace('{n}', rows.length)}
          confirmLabel={t('bsk_clear')}
          onConfirm={empty}
          onClose={() => setConfirming('')}
        />
      ) : confirming ? (
        <ConfirmDialog
          title={t('bsk_remove_title')}
          message={t('bsk_remove_msg')}
          confirmLabel={t('bsk_remove')}
          onConfirm={() => remove(confirming)}
          onClose={() => setConfirming('')}
        />
      ) : null}
    </div>
  );
}
